import { z } from "zod";

const groupBy = z.enum([
  "taskName",
  "taskCategory",
  "taskType",
  "client",
  "lineOfBusiness",
  "engagementType",
  "assignedTo",
  "templateName",
]);

const sortDir = z.enum(["asc", "desc"]);
const tab = z.enum(["aggregate", "instances"]);
const slownessBucket = z.enum(["OnTrack", "Slow", "Critical", "InsufficientData", "InProgress"]);
const requestStatus = z.enum([
  "Unknown",
  "Completed",
  "InProgress",
  "Draft",
  "PendingValidation",
  "RequiresReview",
  "PendingOnboarding",
  "OnHold",
  "Stalled",
  "AwaitingConsultant",
  "Discarded",
  "Cancelled",
]);
const actorRole = z.enum(["Consultant", "SubvendorWorker", "SubvendorOrg", "ClientService", "AccountManager", "Internal", "Other"]);
const pageSize = z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(200)]);

export const taskEfficiencySchema = z.object({
  tab: tab.default("aggregate"),
  // Aggregate-tab state
  groupBy: z.array(groupBy).default(["taskName"]),
  minSample: z.number().int().nonnegative().default(5),
  sort: z.string().default("median"),
  dir: sortDir.default("desc"),
  // Instances-tab state
  instanceSort: z.string().default("duration"),
  instanceDir: sortDir.default("desc"),
  page: z.number().int().positive().default(1),
  pageSize: pageSize.default(50),
  // Date window
  dateRange: z.number().int().positive().default(30),
  from: z.string().optional(),
  to: z.string().optional(),
  // Filters (request-context)
  requestType: z.array(z.string()).default([]),
  status: z.array(requestStatus).default([]),
  client: z.array(z.string()).default([]),
  lob: z.array(z.string()).default([]),
  eng: z.array(z.string()).default([]),
  assigned: z.array(z.string()).default([]),
  country: z.array(z.string()).default([]),
  bucket: z.array(slownessBucket).default([]),
  rework: z.boolean().default(false),
  // Request-type buckets: Onboarding = RequestType="Onboarding" ≥7; Redeployment = Onboarding ≤6
  // OR raw RequestType="Redeployment". Opt-in.
  onboardingsOnly: z.boolean().default(false),
  redeployment: z.boolean().default(false),
  // Filters (task-specific)
  taskCategory: z.array(z.string()).default([]),
  taskName: z.array(z.string()).default([]),
  actorRole: z.array(actorRole).default([]),
  // Derived task status (same rule the Detail screen's pills use — see
  // TasksTable.deriveTaskStatus). Empty = no filter. Raw Status lags so we never expose it.
  taskStatus: z
    .array(z.enum(["Completed", "InProgress", "NotStarted", "Discarded"]))
    .default([]),
  // Drilldown (which group from Aggregate tab navigated us here)
  drillKeys: z.array(groupBy).default([]),
  drillVals: z.array(z.string()).default([]),
  // Duration unit for the whole screen (aggregate stats + instance durations). Shared across tabs.
  durationUnit: z.enum(["bd", "cd"]).default("bd"),
  // Free-text search: live term + committed pills (AND together). Shared across tabs.
  q: z.string().default(""),
  qPills: z.array(z.string()).default([]),
});

export type TaskEfficiencyState = z.infer<typeof taskEfficiencySchema>;
export const TASK_DEFAULTS = taskEfficiencySchema.parse({});
